import { Link } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Empty, Row, Spin, Typography } from 'antd';
import {
    AppstoreOutlined,
    CalendarOutlined,
    DashboardOutlined,
    DollarOutlined,
    InfoCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useClassWrapper, useFetchWrapper } from '_helpers';
import { ROLE_GROUPS } from '../_helpers/roles';
import { classPickerVisibleAtom } from '_state';
import dayjs from '_helpers/datetime';

export { Home };

const { Text } = Typography;

const ROLE_LABELS = {
    manager: 'Quản lý',
    headmaster: 'Hiệu trưởng',
    teacher: 'Giáo viên',
    student: 'Học viên',
    admin: 'Admin',
};

function Home() {
    const userData = JSON.parse(localStorage.getItem('userData') || 'null');
    const fetchWrapper = useFetchWrapper();
    const classWrapper = useClassWrapper();
    const [, setDrawerVisible] = useRecoilState(classPickerVisibleAtom);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetchWrapper.get('/api/home/summary', null, null);
            const json = await res.json();
            if (json.status === 'Success') setSummary(json.message);
            else setError(json.message || 'Không tải được trang chủ');
        } catch (e) {
            setError('Không tải được trang chủ');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!userData) return null;
    if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
    if (error) {
        return (
            <div className="att2-page soft-in">
                <Empty description={error} />
                <div style={{ textAlign: 'center', marginTop: 12 }}><Button onClick={load}>Thử lại</Button></div>
            </div>
        );
    }

    const isStaff = ROLE_GROUPS.STAFF.includes(userData.role);
    const showPicker = () => {
        classWrapper.getClassList();
        setDrawerVisible(true);
    };

    return (
        <div className="page-enter home2-page">
            <div className="home2-head">
                <div>
                    <h1>Trang chủ</h1>
                    <Text type="secondary">{userData.name} · {ROLE_LABELS[userData.role] || userData.role}</Text>
                </div>
                {isStaff &&
                    <Button type={classWrapper.curClass ? 'default' : 'primary'} icon={<AppstoreOutlined />} onClick={showPicker}>
                        {classWrapper.curClass ? 'Đổi lớp' : 'Chọn lớp'}
                    </Button>}
            </div>
            {isStaff
                ? <StaffHome summary={summary || {}} curClass={classWrapper.curClass} onChooseClass={showPicker} />
                : <StudentHome summary={summary || {}} />}
        </div>
    );
}

function StaffHome({ summary, curClass, onChooseClass }) {
    const staff = summary.staff || {};
    const manager = summary.manager || null;
    const sessions = staff.todaySessions || [];
    const scheduleOnly = staff.scheduleOnly || [];
    const unfinished = staff.unfinishedAttendance || [];

    return (
        <>
            {!curClass &&
                <Card className="home2-banner">
                    <InfoCircleOutlined /> <span>Chưa chọn lớp hiện tại.</span>
                    <Button size="small" onClick={onChooseClass}>Chọn lớp</Button>
                </Card>}

            <Row gutter={[12, 12]}>
                <MetricCard icon={<CalendarOutlined />} label="Buổi hôm nay" value={sessions.length} />
                <MetricCard icon={<WarningOutlined />} label="Điểm danh chưa xong" value={unfinished.length} />
                <MetricCard icon={<DashboardOutlined />} label="Lịch mẫu chưa tạo buổi" value={scheduleOnly.length} />
                {manager && <MetricCard icon={<DollarOutlined />} label="Học phí cần xử lý" value={(manager.tuition?.owed || 0) + (manager.tuition?.renew || 0)} />}
            </Row>

            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col xs={24} lg={14}>
                    <Widget title="Hôm nay" empty="Không có buổi nào hôm nay">
                        {sessions.map(s => (
                            <Link className="home2-row" key={s._id} to={`/${s.class_id}/attendance?sessionId=${s._id}`}>
                                <span>
                                    <b>{s.class_name}</b>
                                    <small>{s.lesson_name} · {dayjs(s.date).format('HH:mm')}</small>
                                </span>
                                {s.cancelled ? <em>Đã hủy</em> : s.unfinished ? <em>Cần hoàn tất</em> : <em>OK</em>}
                            </Link>
                        ))}
                        {scheduleOnly.map((s, i) => (
                            <Link className="home2-row muted" key={`tpl-${i}`} to={`/schedule?classId=${s.class_id}`}>
                                <span>
                                    <b>{s.class_name}</b>
                                    <small>Lịch mẫu · {s.start}</small>
                                </span>
                                <em>Chưa tạo buổi</em>
                            </Link>
                        ))}
                    </Widget>
                </Col>
                <Col xs={24} lg={10}>
                    <Widget title="Lớp nhanh" empty="Chưa có lớp">
                        {(staff.currentClassShortcuts || []).map(c => (
                            <Link className="home2-row" key={c.class_id} to={`/${c.class_id}/dashboard`}>
                                <span><b>{c.class_name}</b><small>{c.is_trial ? 'Học thử' : 'Chính thức'}</small></span>
                                <em>Mở</em>
                            </Link>
                        ))}
                    </Widget>
                </Col>
            </Row>

            {manager &&
                <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                    <ManagerList title="Chưa gán giáo viên dạy" items={manager.unassignedClasses} to={c => `/manager?classId=${c.class_id}`} />
                    <ManagerList title="Chưa có lịch hợp lệ" items={manager.classesWithoutSchedule} to={c => `/schedule?classId=${c.class_id}`} />
                    <ManagerList title="Học thử cần theo dõi" items={manager.trialFollowups} to={c => `/manager/students?status=trial&classId=${c.class_id}`} />
                    <Col xs={24} md={12} xl={6}>
                        <Card className="home2-widget" title="Học phí">
                            <Link className="home2-row" to="/tuition?status=owed"><span><b>Còn nợ</b></span><em>{manager.tuition?.owed || 0}</em></Link>
                            <Link className="home2-row" to="/tuition?status=renew"><span><b>Cần gia hạn</b></span><em>{manager.tuition?.renew || 0}</em></Link>
                        </Card>
                    </Col>
                </Row>}
        </>
    );
}

function StudentHome({ summary }) {
    const data = summary.student || {};
    const cls = data.currentClass;
    if (!cls) return <Card><Empty description="Bạn chưa có lớp học" /></Card>;
    return (
        <Row gutter={[12, 12]}>
            <Col xs={24} lg={8}>
                <Card className="home2-widget" title="Lớp hiện tại">
                    <Link className="home2-row" to={`/${cls.class_id}/home-assignments`}><span><b>{cls.class_name}</b><small>Bài tập</small></span><em>Mở</em></Link>
                    <Link className="home2-row" to={`/${cls.class_id}/attendance`}><span><b>Điểm danh</b></span><em>Mở</em></Link>
                </Card>
            </Col>
            <Col xs={24} lg={8}>
                <Card className="home2-widget" title="Điểm danh">
                    <div className="home2-big">{data.attendance?.present || 0}/{data.attendance?.total || 0}</div>
                    <Text type="secondary">Muộn {data.attendance?.late || 0} · Vắng {data.attendance?.absent || 0}</Text>
                </Card>
            </Col>
            <Col xs={24} lg={8}>
                <Card className="home2-widget" title="Bài tập">
                    {data.nextHomework
                        ? <Link className="home2-row" to={`/${cls.class_id}/home-assignments?assignmentId=${data.nextHomework._id}`}>
                            <span><b>{data.nextHomework.title}</b><small>{data.nextHomework.due_date ? dayjs(data.nextHomework.due_date).format('DD/MM HH:mm') : 'Chưa có hạn'}</small></span><em>Nộp</em>
                        </Link>
                        : <Empty description="Không còn bài cần nộp" />}
                    {data.latestReviewed &&
                        <Link className="home2-row" to={`/${cls.class_id}/home-assignments?assignmentId=${data.latestReviewed.assignment_id}`}>
                            <span><b>Bài mới chấm</b><small>{data.latestReviewed.grade != null ? `${data.latestReviewed.grade}/10` : 'Có nhận xét'}</small></span><em>Xem</em>
                        </Link>}
                </Card>
            </Col>
        </Row>
    );
}

function MetricCard({ icon, label, value }) {
    return (
        <Col xs={12} md={6}>
            <Card className="home2-metric">
                <span>{icon}</span>
                <div><b>{value}</b><small>{label}</small></div>
            </Card>
        </Col>
    );
}

function Widget({ title, empty, children }) {
    const arr = Array.isArray(children) ? children.filter(Boolean) : [children].filter(Boolean);
    return <Card className="home2-widget" title={title}>{arr.length ? arr : <Empty description={empty} />}</Card>;
}

function ManagerList({ title, items = [], to }) {
    return (
        <Col xs={24} md={12} xl={6}>
            <Card className="home2-widget" title={title}>
                {items.length
                    ? items.slice(0, 5).map(c => (
                        <Link className="home2-row" key={c.class_id} to={to(c)}>
                            <span><b>{c.class_name}</b><small>{c.class_id}</small></span><em>Mở</em>
                        </Link>
                    ))
                    : <Empty description="Không có mục nào" />}
            </Card>
        </Col>
    );
}
